import api from 'src/utils/ajax';
import { decamelizeKeys } from 'src/utils/decamelize-keys';
import { populateRelationshipFromRequest } from 'src/utils/populateRelationship';

import { User } from '../user/_types';
import { fetchUsers } from '../user/user';

import { PaymentMethod } from './_types';
import { normalizePaymentMethod } from './normalizers';

export const createPaymentMethod = async (
  pm: Partial<PaymentMethod>
): Promise<PaymentMethod> => {
  const response = await api.post(
    '/api/v4/fp/payment_methods',
    decamelizeKeys(pm)
  );
  return normalizePaymentMethod(response.data);
};

export const makePaymentMethodDefault = async (
  paymentMethodId: PaymentMethod['id']
): Promise<PaymentMethod> => {
  const response = await api.post(
    `/api/v4/fp/payment_methods/${paymentMethodId}/default`
  );
  return normalizePaymentMethod(response.data);
};

export const fetchAccessiblePaymentMethodsQueryKey = (
  userId: User['id'] | undefined
) => ['paymentMethods', 'accessible', userId];

export const fetchAccessiblePaymentMethods = async (
  userId: User['id']
): Promise<PaymentMethod[]> => {
  const response = await api.get('/api/v4/fp/payment_methods/accessible', {
    params: { user_id: userId },
  });

  let paymentMethods = response.data.map(normalizePaymentMethod);

  paymentMethods = await attachUsersToPaymentMethods(paymentMethods);

  return paymentMethods;
};

export const fetchPaymentMethods = async (
  userId: User['id']
): Promise<PaymentMethod[]> => {
  const response = await api.get('/api/v4/fp/payment_methods', {
    params: { user_id: userId },
  });
  return response.data.map(normalizePaymentMethod);
};

export const fetchPaymentMethod = async (
  userId: User['id'],
  paymentMethodId: PaymentMethod['id'],
  pollingVerification: boolean = false
): Promise<PaymentMethod> => {
  const response = await api.get(
    `/api/v4/fp/payment_methods/${paymentMethodId}`,
    { params: { user_id: userId, polling_verification: pollingVerification || null } }
  );
  return normalizePaymentMethod(response.data);
};

const attachUsersToPaymentMethods = async (
  paymentMethods: PaymentMethod[]
): Promise<PaymentMethod[]> => {
  return await populateRelationshipFromRequest({
    list: paymentMethods,
    idKey: 'userId',
    newKey: 'user',
    func: async (ids) => {
      const users = await fetchUsers({ ids });
      return users;
    },
  });
};

export const deletePaymentMethod = async (
  paymentMethodId: PaymentMethod['id']
): Promise<void> => {
  await api.delete(`/api/v4/fp/payment_methods/${paymentMethodId}`);
};

interface TerminalConnectionToken {
  secret: string;
}

export const fetchTerminalConnectionToken = async (): Promise<string> => {
  const response = await api.post<TerminalConnectionToken>(
    '/api/v4/fbm/card_readers/connect_token'
  );
  return response.data.secret;
};
